import { useIntl } from '@/intl'
import { PricesUseCasesContext } from '@/scopes/prices/components/PricesUseCasesContext'
import { DayByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/DayByWeeks'
import { NavigationByWeeksContext } from '@/scopes/prices/components/PriceTableByWeeks/NavigationByWeeksContext'
import { PriceCellByWeeks } from '@/scopes/prices/components/PriceTableByWeeks/PriceCellByWeeks'
import {
  getPriceRangeSelector,
  getPricesSelector,
  pricesResponseSelector,
  selectedPriceIdSelector,
} from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { radius, Table, tokPx, UnavailableContentDialog } from '@sunwebgroup/ui-components'
import { Button } from '@sunwebgroup/ui-components/uds'
import { eachDayOfInterval, format, isBefore, parseISO } from 'date-fns'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { css, styled } from 'styled-components'

const DayHeader = styled(Table.Header)<{ $isHighlighted: boolean, $isSelected: boolean }>`
  padding: 0 ${tokPx('primitives_space_12')} 0 ${tokPx('primitives_space_12')};
  height: 66px;
  background: white;
  border: none;
  width: 100%;
  &:before {
    width: unset;
  }
`

const PriceTableColumn = styled(Table.Col)<{ $disabled?: boolean }>`
    margin-right: ${tokPx('primitives_space_6')};
    padding: 2.5px ${tokPx('primitives_space_6')};
    background: white;
    border-radius:  ${radius('primitives_corner_radius_m')};
    width: 100%;
    position:relative;
    ${({ $disabled }) => $disabled && css`
        opacity: 0.38;
    `}
`

const PriceTableBody = styled(Table.Body)`
    width: 100%;
    & div:last-child {
      margin-right: 0;
    }
  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    margin-right: ${tokPx('primitives_space_6')};
  }
`

const PriceTableRoot = styled(Table.Root)`
    width: 100%;
`
export function GridByWeeks() {
  const intl = useIntl()
  const { prices, pricesByDurationAndDateByWeeks } = usePricesSelector(pricesResponseSelector)
  const { error: isError } = usePricesSelector(getPricesSelector)
  const { dateFrom, dateTo } = usePricesSelector(getPriceRangeSelector)
  const selectedPriceId = usePricesSelector(selectedPriceIdSelector)
  const { selectPrice } = React.useContext(PricesUseCasesContext)
  const { navigationDateFrom, navigationDateTo } = React.useContext(NavigationByWeeksContext)

  const selectedPrice = React.useMemo(
    () => prices?.find(price => price.id === selectedPriceId),
    [prices, selectedPriceId],
  )

  const onPriceSelect = React.useCallback(
    (newSelectedPriceId: string) => {
      selectPrice(newSelectedPriceId)
    },
    [selectPrice],
  )

  if (!dateFrom || !dateTo || !navigationDateFrom || !navigationDateTo || !prices || isEmpty(prices)) {
    return null
  }

  if (isError) {
    return (
      <UnavailableContentDialog
        title={intl.formatMessage({ id: 'prices_error_title' })}
        message={intl.formatMessage({ id: 'prices_error_message' })}
      >
        <Button onClick={() => window.location.reload()}>
          {intl.formatMessage({ id: 'prices_error_reload_button' })}
        </Button>
      </UnavailableContentDialog>
    )
  }

  const datesToDisplay = eachDayOfInterval({ start: navigationDateFrom, end: navigationDateTo }).map(date =>
    format(date, 'yyyy-MM-dd'),
  )

  const today = format(new Date(), 'yyyy-MM-dd')
  const disableColumn = (date: string) => isBefore(parseISO(date), parseISO(today))

  return (
    <PriceTableRoot data-qa="price-table">
      <PriceTableBody>
        {datesToDisplay.map(date => (
          <PriceTableColumn key={date} $disabled={disableColumn(date)}>
            <DayHeader $isHighlighted={false} $isSelected={date === selectedPrice?.departureDate}>
              <DayByWeeks date={date} />
            </DayHeader>
            {!disableColumn(date) && Object.keys(pricesByDurationAndDateByWeeks!).map((duration: string) => (
              <PriceCellByWeeks
                key={duration + date}
                price={pricesByDurationAndDateByWeeks![Number(duration)]?.[date]}
                onClick={onPriceSelect}
                isSelected={selectedPrice?.departureDate === date && selectedPrice?.duration.toString() === duration}
              />
            ))}
          </PriceTableColumn>
        ))}
      </PriceTableBody>
    </PriceTableRoot>
  )
}
